import { Address, Co6TransactionTypes, IPersonalAddressState } from '@interfaces';
import { VgEmploymentState } from '@interfaces/models/open-account-mfes/employment-mfe.model';
import { MFEAddress } from '@interfaces/models/open-account-mfes/mfe-common.interfaces';
import { AccountTriageState } from '@interfaces/state';
import { createSelector } from '@ngrx/store';
import { CooDocumentInformation } from '@shared/services/hydration/ku4-draft.models';
import {
  getDecedentInfoState,
  getFeatureToggleState,
  getInternalTransferState,
  getMfePersonalAddressState,
  getMinorTransferState,
  getOpenAccountState,
  getSelectedClientAccountsState,
  getTriageState,
} from '@store/reducers';
import { DecedentInfoState } from '@store/reducers/decedent-info/decedent-info.reducer';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { getClientName } from '@store/reducers/personal-data/personal-data.reducer';
import { ITaxDocumentUploadState } from '@store/reducers/tax-document-upload/tax-document-upload.reducer';
import { TrustState } from '@store/reducers/trust/trust.reducer';
import { selectBeneficiaryState } from '@store/selectors/beneficiaries/beneficiaries.selectors';
import { selectSelectedPositions } from '@store/selectors/cost-basis/cost-basis.selectors';
import { selectDocumentUploadAccountTypes } from '@store/selectors/document-upload/document-upload.selectors';
import { selectEstateState } from '@store/selectors/estate/estate.selectors';
import { selectGiftingState } from '@store/selectors/gifting/gifting.selectors';
import { selectIncomeNetWorth } from '@store/selectors/income-net-worth/income-net-worth.selectors';
import { selectObjectives } from '@store/selectors/investment-objectives/investment-objectives.selectors';
import { selectVGHasLetter } from '@store/selectors/letter-upload/letter-upload.selectors';
import { selectMfePersonalInfoClientPersonalInfo } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectMinorTransferState } from '@store/selectors/minor-transfer/minor-transfer.selectors';
import { selectEmploymentMfeDataByOwnerIndex } from '@store/selectors/open-account/open-account.selectors';
import {
  selectClientName,
  selectClientSegment,
  selectPersonalDataState,
} from '@store/selectors/personal-data/personal-data.selectors';
import { selectMfePersonalAddress } from '@store/selectors/personal-info-address/personal-info-address.selectors';
import { selectMfePhone } from '@store/selectors/personal-phone/personal-phone.selectors';
import {
  selectIsTaxDocumentInState,
  selectTaxCheckboxSelections,
} from '@store/selectors/tax-document-upload/tax-document-upload.selectors';
import {
  selectAllTransactions,
  selectCurrentTransaction,
} from '@store/selectors/transaction-state/transaction-state.selectors';
import { selectTrustState } from '@store/selectors/trust/trust.selectors';
import { selectTrustTaxInfoState } from '@store/selectors/trust-tax-info/trust-tax-info.selector';

import { selectDraftDocumentFiles } from './draft-documents.selectors';

// The personalData block is byte-identical across the inheritance, multi-inheritance
// and minor payloads. Extracted so adding a field (e.g. a new KYC attribute) is a
// one-line change instead of three that can silently drift apart.
export const buildPersonalData = (
  clientPersonalInfoData: MfePersonalInfoClientState['personalInfo'],
  segment: string,
) => ({
  ownerName: getClientName(clientPersonalInfoData),
  segment,
  dateOfBirth: clientPersonalInfoData.birthDate,
  citizenshipStatus: clientPersonalInfoData.citizenshipStatus,
  email: clientPersonalInfoData.email,
  gender: clientPersonalInfoData.gender,
  countryOfCitizenship: clientPersonalInfoData.countryOfCitizenship,
  suffix: clientPersonalInfoData.suffix,
  ssn: clientPersonalInfoData.ssn,
});

// co0-draft-library's dehydration input expects `tin`/`streetAddress`, but the
// FE's DecedentInfoState uses `decedentId_private`/`addressLine1` — translate
// here so the decedent's TIN and street address actually reach the draft.
function mapDecedentInfoForDraft(decedentInfo: DecedentInfoState) {
  return {
    ...decedentInfo,
    tin: decedentInfo.decedentId_private,
    streetAddress: decedentInfo.addressLine1,
  };
}

// co0-draft-library's mapMaritalStatus dehydrator reads triage.isDecedentMarried, but
// Account Triage stores the answer under AccountTriageState.wasMarried — add the
// dehydrator's expected name alongside the original so the marital status actually
// reaches the draft. Omit the key entirely when wasMarried is undefined (no accountTriage
// yet), rather than spreading an explicit `isDecedentMarried: undefined` — some dehydrators
// check key presence, not truthiness, and would otherwise treat "unanswered" as "cleared".
function mapAccountTriageForDraft(accountTriage: AccountTriageState | undefined) {
  return {
    ...accountTriage,
    ...(accountTriage?.wasMarried !== undefined
      ? { isDecedentMarried: accountTriage.wasMarried }
      : {}),
  };
}

// The trust-tax-info page stores grantor citizenship under TrustState.grantorQuestion,
// but co0-draft-library's dehydration input expects it under TrustInfo.grantorCountry —
// translate here so the grantor's country of citizenship actually reaches the draft.
function mapTrustInfoForDraft(
  trust: TrustState | undefined,
): (TrustState & { grantorCountry?: string }) | undefined {
  if (!trust) {
    return trust;
  }

  return {
    ...trust,
    grantorCountry: trust.grantorQuestion,
  };
}

// The L8/ITW page stores 3 mutually exclusive checkboxes, but co0-draft-library's
// dehydration input expects a single taxWaiverStatus value — collapse one into
// the other. Inverse of buildTaxCheckboxSelections in inheritance.hydrator.ts,
// which restores the checkboxes from a hydrated draft.
function mapTaxWaiverStatusForDraft(
  checkboxSelections: ITaxDocumentUploadState['checkboxSelections'],
): CooDocumentInformation['taxWaiverStatus'] {
  if (checkboxSelections.mailingForm) {
    return 'MAILING';
  }
  if (checkboxSelections.beneficiarySubmitted) {
    return 'ALREADY_SUBMITTED';
  }
  if (checkboxSelections.noFormNeeded) {
    return 'NOT_NEEDED';
  }
  return undefined;
}

// vgHasLetter defaults to false in the reducer even when the user never visited
// the Letter Upload page, so sending it unconditionally would make every draft
// claim "no" rather than "unanswered". Only send it when the checkbox was
// actually checked, so an unvisited page stays absent from the draft.
function mapVanguardHasLetterForDraft(vgHasLetter: boolean): true | undefined {
  return vgHasLetter ? true : undefined;
}

// co0-draft-library reads the employer address as addressLine1/zip, but the employment MFE
// stores it as street/zipCode. Its mapEmployerAddress bails on a missing addressLine1, so
// sending the MFE shape unchanged dropped the whole address and the draft never carried it
// (ONBCOO-16510). Translate at the boundary, as the personal address already does.
function mapEmployerAddressForDraft(address: MFEAddress) {
  return {
    addressLine1: address.street,
    city: address.city,
    state: address.state,
    zip: address.zipCode,
  };
}

function mapEmploymentForDraft(employment: Partial<VgEmploymentState> | undefined) {
  const status = employment?.employmentStatus;

  if (!status) {
    return employment;
  }

  const { streetAddress, ...restOfStatus } = status;

  return {
    ...employment,
    employmentStatus: {
      ...restOfStatus,
      ...(streetAddress ? { streetAddress: mapEmployerAddressForDraft(streetAddress) } : {}),
    },
  };
}

function mapAddressForDraft(personalAddress: IPersonalAddressState) {
  return {
    personalAddress: {
      streetAddress: mapMfeAddressToDraft(personalAddress?.streetAddress),
      mailingAddress: mapMfeAddressToDraft(personalAddress?.mailingAddress),
    },
  };
}

function mapMfeAddressToDraft(addr: Address | undefined) {
  if (!addr) {
    return undefined;
  }
  return {
    street: addr.street,
    city: addr.city,
    state: addr.state,
    zip: addr.zipCode,
    ...(addr.zipPlus4Code ? { zipPlus4: addr.zipPlus4Code } : {}),
  };
}

export const getMultiDraftPayload = createSelector(
  selectMfePersonalInfoClientPersonalInfo,
  selectPersonalDataState,
  getDecedentInfoState,
  getFeatureToggleState,
  getTriageState,
  selectEmploymentMfeDataByOwnerIndex(0),
  getMfePersonalAddressState,
  selectMfePhone,
  selectAllTransactions,
  selectDraftDocumentFiles,
  selectVGHasLetter,
  selectTaxCheckboxSelections,
  selectIsTaxDocumentInState,
  selectSelectedPositions,
  selectIncomeNetWorth,
  selectDocumentUploadAccountTypes,

  (
    clientPersonalInfoData,
    personalData,
    decedentInfo,
    featureToggles,
    triageState,
    employmentFeature,
    addressState,
    phone,
    transactions,
    draftDocumentFiles,
    vanguardHasLetter,
    taxCheckboxSelections,
    taxFileUploaded,
    selectedPositions,
    incomeNetWorth,
    declaredAccountTypes,
    // eslint-disable-next-line max-params
  ) => {
    // Global slices the user fills in before any transaction exists (decedent-info,
    // document-upload, personal-info pages precede account-triage where transactions
    // are first created). These must reach the draft regardless of transaction count.
    const globalConfig = {
      personalData: buildPersonalData(clientPersonalInfoData, personalData.segment),
      declaredAccountTypes,
      decedentInfo: mapDecedentInfoForDraft(decedentInfo),
      employment: mapEmploymentForDraft(employmentFeature),
      incomeNetWorth,
      address: mapAddressForDraft(addressState.personalAddress),
      phone,
      ...draftDocumentFiles,
      vanguardHasLetter: mapVanguardHasLetterForDraft(vanguardHasLetter),
      taxCheckboxSelections,
      taxFileUploaded,
      taxWaiverStatus: mapTaxWaiverStatusForDraft(taxCheckboxSelections),
      featureToggles,
    };

    // Before account-triage there are no transactions yet — emit a single synthetic
    // payload carrying only the global data so the draft is populated from page one.
    if (!Array.isArray(transactions) || transactions.length === 0) {
      return {
        payloads: [
          { transactionType: Co6TransactionTypes.inheritance, config: { ...globalConfig } },
        ],
      };
    }

    const payloads = transactions.map((transaction) => ({
      transactionType: Co6TransactionTypes.inheritance,
      config: {
        ...globalConfig,
        triage: {
          ...triageState,
          ...mapAccountTriageForDraft(transaction.accountTriage),
          iraOption: transaction.selectedClientAccounts.transferTo?.iraOption,
        },
        selectedAccs: transaction.selectedClientAccounts,
        gifting: { selectedPositions },
        estateInfo: transaction.estate,
        trust: mapTrustInfoForDraft(transaction.trust),
        trustTaxInfo: transaction.trustTaxInfo,
        beneficiaries: { otherBenes: transaction.beneficiaries },
        settlementFund: transaction.settlementFund,
        investmentObjectives: transaction.investmentObjectives,
      },
    }));

    return { payloads };
  },
);

export const getDraftPayload = createSelector(
  selectMfePersonalInfoClientPersonalInfo,
  selectPersonalDataState,
  getDecedentInfoState,
  getFeatureToggleState,
  getTriageState,
  getOpenAccountState,
  selectEmploymentMfeDataByOwnerIndex(0),
  selectMfePersonalAddress,
  selectMfePhone,
  getSelectedClientAccountsState,
  selectGiftingState,
  selectSelectedPositions,
  selectDraftDocumentFiles,
  selectVGHasLetter,
  selectTaxCheckboxSelections,
  selectIsTaxDocumentInState,
  selectIncomeNetWorth,
  selectEstateState,
  selectTrustState,
  selectTrustTaxInfoState,
  selectBeneficiaryState,
  selectDocumentUploadAccountTypes,
  selectCurrentTransaction,

  (
    clientPersonalInfoData,
    personalData,
    decedentInfo,
    featureToggles,
    triageState,
    openAccountState,
    employmentFeature,
    address,
    phone,
    selectedClientAccounts,
    giftingState,
    selectedPositions,
    draftDocumentFiles,
    vanguardHasLetter,
    taxCheckboxSelections,
    taxFileUploaded,
    incomeNetWorth,
    estateInfo,
    trustState,
    trustTaxInfoState,
    beneficiaryState,
    declaredAccountTypes,
    currentTransaction,
    // eslint-disable-next-line max-params
  ) => ({
    transactionType: Co6TransactionTypes.inheritance,
    config: {
      personalData: buildPersonalData(clientPersonalInfoData, personalData.segment),
      declaredAccountTypes,
      decedentInfo: mapDecedentInfoForDraft(decedentInfo),
      triage: {
        ...triageState,
        ...mapAccountTriageForDraft(currentTransaction?.accountTriage),
        iraOption: selectedClientAccounts.transferTo?.iraOption,
      },
      selectedAccs: selectedClientAccounts,
      gifting: { ...giftingState, selectedPositions },
      estateInfo,
      trust: mapTrustInfoForDraft(trustState),
      trustTaxInfo: trustTaxInfoState,
      beneficiaries: beneficiaryState,
      settlementFund: openAccountState.settlementFundChoice,
      employment: mapEmploymentForDraft(employmentFeature),
      incomeNetWorth,
      address: mapAddressForDraft(address),
      phone,
      ...draftDocumentFiles,
      vanguardHasLetter: mapVanguardHasLetterForDraft(vanguardHasLetter),
      taxCheckboxSelections,
      taxFileUploaded,
      taxWaiverStatus: mapTaxWaiverStatusForDraft(taxCheckboxSelections),
      featureToggles,
    },
  }),
);

export const getITDraftPayload = createSelector(
  selectClientName,
  selectClientSegment,
  getFeatureToggleState,
  getInternalTransferState,
  getOpenAccountState,
  selectSelectedPositions,

  (
    ownerName,
    segment,
    featureToggles,
    internalTransferState,
    openAccountState,
    selectedPositions,
  ) => ({
    transactionType: Co6TransactionTypes.internalTransfer,
    itConfig: {
      personalData: { ownerName, segment },
      featureToggles,
      internalTransfer: internalTransferState,
      openAccountState,
      selectedPositions,
    },
  }),
);

// minor (UGMA/UTMA) draft payload. Mirrors the getDraftPayload
export const getMinorDraftPayload = createSelector(
  selectMfePersonalInfoClientPersonalInfo,
  selectPersonalDataState,
  getFeatureToggleState,
  getTriageState,
  selectMinorTransferState,
  getSelectedClientAccountsState,
  getOpenAccountState,
  selectEmploymentMfeDataByOwnerIndex(0),
  selectMfePersonalAddress,
  selectMfePhone,
  selectIncomeNetWorth,
  // Not selectInvestmentObjectives: it wraps the flat map in { objectives }, and the library
  // keeps only literally-`true` entries, so every objective was silently dropped.
  selectObjectives,
  selectDraftDocumentFiles,
  selectVGHasLetter,
  (
    clientPersonalInfoData,
    personalData,
    featureToggles,
    triageState,
    minorTransferState,
    selectedClientAccounts,
    openAccountState,
    employmentFeature,
    address,
    phone,
    incomeNetWorth,
    investmentObjectives,
    draftDocumentFiles,
    vanguardHasLetter,
    // eslint-disable-next-line max-params
  ) => ({
    transactionType: Co6TransactionTypes.minor,
    config: {
      personalData: buildPersonalData(clientPersonalInfoData, personalData.segment),
      triage: triageState,
      selectedAccs: selectedClientAccounts,
      minorTransfer: minorTransferState,
      settlementFund: openAccountState.settlementFundChoice,
      employment: mapEmploymentForDraft(employmentFeature),
      incomeNetWorth,
      investmentObjectives,
      address: mapAddressForDraft(address),
      phone,
      // The minor flow has no document or tax upload page, so only the letter bucket applies.
      letterFiles: draftDocumentFiles.letterFiles,
      // Mirror getDraftPayload: send the flag only when actually checked, so an unvisited
      // letter page stays absent from the draft rather than claiming "no".
      vanguardHasLetter: mapVanguardHasLetterForDraft(vanguardHasLetter),
      featureToggles,
    },
  }),
);
